<template>
  <div v-if="user" class="select-course col row">
    <v-select
      v-if="shouldDisplaySchools"
      class="col-6 col-lg-4"
      :placeholder="$store.state.user.langEng ? 'All schools' : 'Alla skolor'"
      :items="schools"
      v-model="selectedSchools"
      solo
      :disabled="disabled"
      background-color="transparent"
      flat
      multiple
      active-class
      dense
    ></v-select>
    <v-select
      v-if="isAdmin"
      class="col-6 col-lg-4"
      :placeholder="$store.state.user.langEng ? 'All classes' : 'Alla klasser'"
      :items="klasses"
      v-model="selectedKlasses"
      solo
      :disabled="disabled"
      flat
      background-color="transparent"
      multiple
      active-class
      dense
    ></v-select>
    <v-select
      class="col-6 col-lg-4"
      :placeholder="$store.state.user.langEng ? 'All courses' : 'Alla kurser'"
      :items="courses"
      v-model="selectedCourses"
      solo
      background-color="transparent"
      flat
      multiple
      :disabled="disabled"
      active-class
      dense
    ></v-select>
    <v-select
      class="col-6 col-lg-4"
      :placeholder="$store.state.user.langEng ? 'No specific person' : 'Ingen specifik person'"
      :items="users"
      v-model="selectedUsers"
      solo
      background-color="transparent"
      flat
      multiple
      v-if="!inEditEvent"
      active-class
      dense
    ></v-select>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

@Component
export default class SelectCourse extends Vue {
  @Prop() disable;
  @Prop() inEditEvent;
  disabled = false;
  hasInitialized = false;

  @Watch("disable", { immediate: true })
  updateDisable(val) {
    this.disabled = val;
  }

  get shouldDisplaySchools() {
    return this.isLiterallyAdmin || this.isAdmin && this.inEditEvent;
  }

  get selectedKlasses() {
    return this.inEditEvent
      ? this.$store.state.calendar.editEvent.klasses.slice()
      : this.$store.state.calendar.selected.klasses.slice();
  }
  set selectedKlasses(klasses) {
    const commitString = this.inEditEvent ? "EditEvent" : "Selected";
    this.$store.commit(`calendar/update${commitString}`, { klasses });
  }

  get selectedSchools() {
    return this.inEditEvent
      ? this.$store.state.calendar.editEvent.schools.slice()
      : this.$store.state.calendar.selected.schools.slice();
  }
  set selectedSchools(schools) {
    const commitString = this.inEditEvent ? "EditEvent" : "Selected";
    this.$store.commit(`calendar/update${commitString}`, { schools });
  }

  get selectedCourses() {
    return this.inEditEvent
      ? this.$store.state.calendar.editEvent.courses.slice()
      : this.$store.state.calendar.selected.courses.slice();
  }
  set selectedCourses(courses) {
    const commitString = this.inEditEvent ? "EditEvent" : "Selected";
    this.$store.commit(`calendar/update${commitString}`, { courses });
  }

  get selectedUsers() {
    const me = this.$store.state.user.loggedInUser;
    if (!me.name) {
        me.name = me.firstname
          ? me.firstname + ' ' + me.lastname
          : me.email || (this.$store.state.user.langEng ? "Unknown name" : "Okänt namn");
    }
    let personnel = this.$store.state.calendar.selected.personnel.slice();

    if (!personnel.length && me && !this.hasInitialized) {
      this.hasInitialized = true;
      if (this.$store.getters.isAdmin) {
        personnel.push(me);
      }

      this.$store.commit(`calendar/updateSelected`, { personnel });
    }

    return personnel;
  }
  set selectedUsers(personnel) {
    this.$store.commit(`calendar/updateSelected`, { personnel });
  }

  get users() {
    let users = this.$store.state.personnel.slice();

    for (let pe of this.selectedUsers) {
      const index = users.findIndex(u => u.id == pe.id);
      if (~index) { users[index] = pe; }
    }

    return users.map(obj => {
      return {
        text: obj.name,
        value: obj
      };
    });
  }

  get courses() {
    let coursesArr = this.selectedKlasses.length
      ? this.selectedKlasses.map(k => k.courses).flat()
      : this.selectedSchools.length
        ? this.getKlassesOfSelectedSchools()
          .map(k => k.courses)
          .flat()
        : this.$store.state.courses.slice();

    if (this.inEditEvent) {
      for (let sc of this.selectedCourses) {
        const index = coursesArr.findIndex(c => c.slug == sc.slug);
        if (~index) {
          coursesArr[index] = sc;
        }
      }
    }

    coursesArr = coursesArr.sort((a, b) => a.name < b.name ? -1 : 1);

    return coursesArr.map(obj => {
      let text = obj.name;
      if (this.$store.getters.isAdmin) {
        text += ` (${obj.klassSlug})`;
      }

      return {
        text,
        value: obj
      };
    });
  }

  get klasses() {
    let klassesArr = this.selectedSchools.length
      ? this.getKlassesOfSelectedSchools()
      : this.$store.state.klassesArr.slice();

    if (this.inEditEvent) {
      for (let sk of this.selectedKlasses) {
        const index = klassesArr.findIndex(k => k.slug == sk.slug);
        if (~index) {
          klassesArr[index] = sk;
        }
      }
    }

    klassesArr.sort((a, b) => {
      if (b.year - a.year != 0) { return b.year - a.year; }
      return a.name < b.name ? -1 : 1;
    });

    return klassesArr.map(obj => {
      return {
        text: obj.name,
        value: obj
      };
    });
  }

  get schools() {
    return this.getSchoolsAsDropdown();
  }

  get user() {
    return this.$store.state.user.loggedInUser;
  }

  get isAdmin() {
    return this.$store.getters.isAdmin;
  }

  get isLiterallyAdmin() {
    return this.$store.getters.isLiterallyAdmin;
  }

  getSchoolsAsDropdown() {
    let schoolsArr =
      this.inEditEvent && this.selectedSchools.length
        ? this.selectedSchools
        : this.$store.state.schools.slice();

    if (this.inEditEvent) {
      for (let ss of this.selectedSchools) {
        const index = schoolsArr.findIndex(c => c.slug == ss.slug);
        if (~index) {
          schoolsArr[index] = ss;
        }
      }
    }

    return schoolsArr.map(obj => {
      return {
        text: obj.name,
        value: obj
      };
    });
  }

  getKlassesOfSelectedSchools() {
    return this.selectedSchools
      .map(s => s.klasses)
      .flat()
      .map(k => this.$store.state.klassesArr.find(storeK => storeK.slug == k.slug) || k);
  }
}
</script>

<style lang="scss">
.select-course {
  .v-input__slot {
    margin: 0;
  }
  .v-text-field__details {
    margin: 0;
  }
}
</style>
